import { defineStore } from 'pinia'
import { computed, onScopeDispose, type Ref, ref, shallowRef, watch } from 'vue'

import type { ActivityItem } from '@/components/chat/ActivityThread/activity-thread-types'
import type { PendingImageHandoff } from '@/composables/usePromptImageAttachments'
import { useStreamedText } from '@/composables/useStreamedText'
import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import type { Subscription } from '@/lib/firebase/subscribe'
import { onRetainedTeardown } from '@/lib/live/retainer'
import {
  deleteAgentThread as repoDeleteAgentThread,
  isChatRateLimitedReason,
  isChatStreamAbortedError,
  isThreadOwnerMismatchReason,
  readThreadPage as repoReadThreadPage,
  subscribeMessages as repoSubscribeMessages,
  subscribePendingActions as repoSubscribePendingActions,
  subscribePendingConnects as repoSubscribePendingConnects,
  subscribePendingQuestions as repoSubscribePendingQuestions,
  subscribeThread as repoSubscribeThread,
  subscribeThreads as repoSubscribeThreads,
  streamAgentMessage as repoStreamAgentMessage,
  type ThreadListSubscription,
  type ThreadPageCursor,
} from '@/repositories/chat'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { useDialogStore } from '@/stores/dialog'
import { useStageStore } from '@/stores/stage'
import { useSurfaceStore } from '@/stores/surface'
import { useToastStore } from '@/stores/toast'
import type { Activity } from '@/types/activity'
import type {
  AgentOutcome,
  AgentStreamChunk,
  ChatMessage,
  ChatThread,
  MessageContext,
  PendingAction,
  PendingActionStatus,
  PendingConnect,
  PendingConnectStatus,
  PendingQuestion,
  PendingQuestionStatus,
  ThreadType,
  ViewContext,
  ViewContextAttachment,
  ViewContextStateFact,
} from '@/types/chat'
import { PENDING_MESSAGE_ID_PREFIX } from '@/types/chat'
import { failed, loaded, loading, type LoadState } from '@/types/load-state'

export const THREAD_PAGE_SIZE = 6

export const RESOLVED_ACTION_LINGER_MS = 6000

interface ChatScope {
  workspaceId: string
  brandId: string
  uid: string
}

type ChatAttachmentKind = 'post' | 'document' | 'insight' | 'design'

/** Attachment kinds that describe only the turn being composed, spent the instant it is sent. */
const ONE_SHOT_ATTACHMENT_KINDS: readonly ChatAttachmentKind[] = ['document', 'design']

/** A post or document the user pointed at, shown as a removable chip; converted by `attachmentToPayload` into the server's `viewContext` attachment. */
export interface ChatAttachment {
  readonly kind: ChatAttachmentKind
  /** The title (post) or title/slug (document); shown in the chip, sent as the attachment's `ref`. */
  readonly ref: string
  readonly excerpt?: string
  /** What the chip and the sent message show in place of `ref` and the excerpt, when those are not words for a person. */
  readonly chip?: string
  /** Facts about the attached item, supplied by the caller that holds it. */
  readonly state?: readonly ViewContextStateFact[]
}

/** Max length of an attachment's `ref`; mirrors the server's `VIEW_CONTEXT_REF_MAX_LENGTH`. */
const VIEW_CONTEXT_REF_MAX_LENGTH = 120
const MAX_VIEW_CONTEXT_STATE_ENTRIES = 4
const VIEW_CONTEXT_STATE_KEY_MAX_LENGTH = 30
const VIEW_CONTEXT_STATE_VALUE_MAX_LENGTH = 60
const VIEW_CONTEXT_EXCERPT_MAX_LENGTH = 500
const VIEW_CONTEXT_DESIGN_EXCERPT_MAX_LENGTH = 1500
const MESSAGE_CONTEXT_SCREEN_MAX_LENGTH = 60

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

/** Converts the attachment into the shape the server's schema accepts, enforcing every cap client-side. */
function attachmentToPayload(attachment: ChatAttachment): ViewContextAttachment {
  const excerpt = attachment.excerpt?.trim()
  const ref = truncate(attachment.ref, VIEW_CONTEXT_REF_MAX_LENGTH)
  if (attachment.kind === 'design') {
    return {
      kind: 'design',
      ref,
      ...(excerpt ? { excerpt: truncate(excerpt, VIEW_CONTEXT_DESIGN_EXCERPT_MAX_LENGTH) } : {}),
    }
  }
  return {
    kind: excerpt ? 'selection' : 'action',
    ref,
    ...(excerpt ? { excerpt: truncate(excerpt, VIEW_CONTEXT_EXCERPT_MAX_LENGTH) } : {}),
  }
}

/** What the design editor tells the chat about the page or element Raldo is asked to change. */
export interface DesignSelectionInput {
  designId: string
  name: string
  page: number
  eid: string | null
  elementName: string | null
  elementHtml: string | null
}

/** The chip words and the excerpt for a design selection: "Page 1 · Headline" and that line plus the element's HTML, capped at what the server takes. */
function designSelectionText(input: DesignSelectionInput): { chip: string; excerpt: string } {
  const chip = [i18n.global.t('design.editor.raldo.chipPage', { page: input.page })]
  if (input.elementName) chip.push(input.elementName)
  const label = chip.join(' · ')
  const html = input.elementHtml?.trim()
  return {
    chip: label,
    excerpt: truncate(html ? `${label}\n${html}` : label, VIEW_CONTEXT_DESIGN_EXCERPT_MAX_LENGTH),
  }
}

/** Keeps an answered ask on screen for a moment so its outcome can be read; a resolution already settled at load does not linger. */
function createResolutionLinger<S extends string>(): {
  lingeringId: Ref<string | null>
  clear: () => void
  track: (items: readonly { id: string; status: S }[]) => void
} {
  const lingeringId = ref<string | null>(null) as Ref<string | null>
  const seenStatuses = new Map<string, S>()
  let timer: ReturnType<typeof setTimeout> | null = null

  function clear(): void {
    if (timer) clearTimeout(timer)
    timer = null
    lingeringId.value = null
    seenStatuses.clear()
  }

  function lingerOver(id: string): void {
    if (timer) clearTimeout(timer)
    lingeringId.value = id
    timer = setTimeout(() => {
      timer = null
      lingeringId.value = null
    }, RESOLVED_ACTION_LINGER_MS)
  }

  function track(items: readonly { id: string; status: S }[]): void {
    for (const item of items) {
      const before = seenStatuses.get(item.id)
      seenStatuses.set(item.id, item.status)
      if (before === 'pending' && item.status !== 'pending') lingerOver(item.id)
    }
  }

  return { lingeringId, clear, track }
}

export const useChatStore = defineStore('chat', () => {
  const livePage = ref<LoadState<ChatThread[]>>(loading()) as Ref<LoadState<ChatThread[]>>
  const olderThreads = ref<ChatThread[]>([])
  const nextThreadCursor = shallowRef<ThreadPageCursor | null>(null)
  const isLoadingMoreThreads = ref(false)
  const threadsError = ref<string | null>(null)
  const activeThreadId = ref<string | null>(null)
  const activeThread = ref<ChatThread | null>(null)
  const subscriptionMessages = ref<LoadState<ChatMessage[]>>(loaded([])) as Ref<
    LoadState<ChatMessage[]>
  >
  const pendingUserMessage = ref<ChatMessage | null>(null)
  /** The persisted assistant message lands while the reveal still runs; hide it until the reveal catches up, or the answer shows twice. */
  const revealWatermark = ref<number | null>(null)
  const messages = computed<LoadState<ChatMessage[]>>({
    get: () => {
      const subState = subscriptionMessages.value
      if (subState.status === 'error') return subState
      if (subState.status === 'loading' && !pendingUserMessage.value) return subState
      const watermark = revealWatermark.value
      const stored = subState.status === 'loaded' ? subState.value : []
      const merged = stored.filter(
        (message) =>
          watermark === null || message.role !== 'assistant' || message.index <= watermark,
      )
      if (pendingUserMessage.value) {
        merged.push(pendingUserMessage.value)
      }
      return loaded(sortByIndex(merged))
    },
    set: (value) => {
      subscriptionMessages.value = value
      pendingUserMessage.value = null
    },
  })
  const pendingActions = ref<PendingAction[]>([])
  const pendingQuestions = ref<PendingQuestion[]>([])
  const pendingConnects = ref<PendingConnect[]>([])
  /** "Skip for now" only hides the card for the session, scoped by connect ID. */
  const dismissedConnectIds = ref<Set<string>>(new Set())
  const sendStatus = ref<'ready' | 'submitting' | 'streaming' | 'error'>('ready')
  const lastTurnCompleted = ref(false)
  const error = ref<string | null>(null)
  const failedTurn = shallowRef<FailedTurn | null>(null)
  const canRetry = computed(
    () =>
      sendStatus.value === 'error' &&
      failedTurn.value !== null &&
      failedTurn.value.threadId === (activeThreadId.value ?? ''),
  )

  const attachment = ref<ChatAttachment | null>(null)
  /** Whether the attachment's payload still has to reach the server; a post chip outlives its turn, so this flag stops re-declaring it. */
  const attachmentPayloadPending = ref(false)
  /** Words the composer should show for the user to send or change; the composer takes them with `takeDraft`. */
  const draft = ref<string | null>(null)
  const designSendGates = new Set<() => Promise<void>>()

  const threads = computed<LoadState<ChatThread[]>>({
    get: () => {
      const state = livePage.value
      if (state.status !== 'loaded') return state
      const seen = new Set<string>()
      const merged: ChatThread[] = []
      for (const thread of [...state.value, ...olderThreads.value]) {
        if (seen.has(thread.id)) continue
        seen.add(thread.id)
        merged.push(thread)
      }
      return loaded(merged)
    },
    set: (value) => {
      livePage.value = value
      olderThreads.value = []
    },
  })

  const hasMoreThreads = computed(() => nextThreadCursor.value !== null)

  const isActiveThreadClosed = computed(() => activeThread.value?.status === 'closed')

  const stream = useStreamedText()
  const streamingText = stream.revealed
  const liveSteps = ref<ActivityItem[]>([])
  const liveOutcomes = ref<AgentOutcome[]>([])

  function clearTurnDisplay(): void {
    stream.reset()
    liveSteps.value = []
    liveOutcomes.value = []
    revealWatermark.value = null
  }

  let activeAbortController: AbortController | null = null

  let pendingThreadType: ThreadType = 'chat'

  let threadsSub: ThreadListSubscription | null = null
  let stopThreadsWatch: (() => void) | null = null
  let cursorFollowsLivePage = true

  let activeThreadSub: Subscription<ChatThread> | null = null
  let stopActiveThreadWatch: (() => void) | null = null

  let messagesSub: Subscription<ChatMessage[]> | null = null
  let stopMessagesWatch: (() => void) | null = null
  let pendingActionsSub: Subscription<PendingAction[]> | null = null
  let stopPendingActionsWatch: (() => void) | null = null
  let pendingQuestionsSub: Subscription<PendingQuestion[]> | null = null
  let stopPendingQuestionsWatch: (() => void) | null = null
  let pendingConnectsSub: Subscription<PendingConnect[]> | null = null
  let stopPendingConnectsWatch: (() => void) | null = null
  let stopReconciliationWatch: (() => void) | null = null

  const actionLinger = createResolutionLinger<PendingActionStatus>()
  const questionLinger = createResolutionLinger<PendingQuestionStatus>()
  const connectLinger = createResolutionLinger<PendingConnectStatus>()

  const visibleAction = computed<PendingAction | undefined>(() => {
    const open = pendingActions.value.find((action) => action.status === 'pending')
    if (open) return open
    const lingering = actionLinger.lingeringId.value
    return lingering ? pendingActions.value.find((action) => action.id === lingering) : undefined
  })

  /** An action always wins over a question. */
  const visibleQuestion = computed<PendingQuestion | undefined>(() => {
    if (visibleAction.value) return undefined
    const open = pendingQuestions.value.find((question) => question.status === 'pending')
    if (open) return open
    const lingering = questionLinger.lingeringId.value
    return lingering
      ? pendingQuestions.value.find((question) => question.id === lingering)
      : undefined
  })

  /** Lowest precedence of the three asks; it never takes over the composer. */
  const visibleConnect = computed<PendingConnect | undefined>(() => {
    if (visibleAction.value || visibleQuestion.value) return undefined
    const open = pendingConnects.value.find(
      (connect) => connect.status === 'pending' && !dismissedConnectIds.value.has(connect.id),
    )
    if (open) return open
    const lingering = connectLinger.lingeringId.value
    return lingering ? pendingConnects.value.find((connect) => connect.id === lingering) : undefined
  })

  const activity = computed<Activity>(() => {
    switch (sendStatus.value) {
      case 'submitting':
      case 'streaming':
        return 'working'
      case 'error':
        return 'failed'
      case 'ready':
        if (visibleAction.value?.status === 'pending') return 'attention'
        if (visibleQuestion.value?.status === 'pending') return 'attention'
        return lastTurnCompleted.value ? 'done' : 'idle'
    }
  })

  function currentScope(): ChatScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    const uid = auth.user?.uid
    if (!workspaceId || !brandId || !uid) return null
    return { workspaceId, brandId, uid }
  }

  function stopThreadsSubscription(): void {
    threadsSub?.stop()
    threadsSub = null
    stopThreadsWatch?.()
    stopThreadsWatch = null
    cursorFollowsLivePage = true
    olderThreads.value = []
    nextThreadCursor.value = null
    isLoadingMoreThreads.value = false
    threadsError.value = null
  }

  function stopActiveThreadSubscription(): void {
    activeThreadSub?.stop()
    activeThreadSub = null
    stopActiveThreadWatch?.()
    stopActiveThreadWatch = null
    activeThread.value = null
  }

  function stopPendingActionsSubscription(): void {
    pendingActionsSub?.stop()
    pendingActionsSub = null
    stopPendingActionsWatch?.()
    stopPendingActionsWatch = null
    pendingActions.value = []
    actionLinger.clear()
  }

  function stopPendingQuestionsSubscription(): void {
    pendingQuestionsSub?.stop()
    pendingQuestionsSub = null
    stopPendingQuestionsWatch?.()
    stopPendingQuestionsWatch = null
    pendingQuestions.value = []
    questionLinger.clear()
  }

  function stopPendingConnectsSubscription(): void {
    pendingConnectsSub?.stop()
    pendingConnectsSub = null
    stopPendingConnectsWatch?.()
    stopPendingConnectsWatch = null
    pendingConnects.value = []
    dismissedConnectIds.value = new Set()
    connectLinger.clear()
  }

  function stopMessagesSubscription(): void {
    messagesSub?.stop()
    messagesSub = null
    stopMessagesWatch?.()
    stopMessagesWatch = null
    stopReconciliationWatch?.()
    stopReconciliationWatch = null
  }

  function sortByIndex(list: ChatMessage[]): ChatMessage[] {
    return [...list].sort((a, b) => a.index - b.index)
  }

  function openThreads(): void {
    if (threadsSub) return

    const scope = currentScope()
    if (!scope) {
      livePage.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    stopThreadsSubscription()
    livePage.value = loading()

    const sub = repoSubscribeThreads({ ...scope, pageSize: THREAD_PAGE_SIZE })
    threadsSub = sub
    stopThreadsWatch = watch(
      sub.state,
      (state) => {
        livePage.value = state
        if (state.status === 'loaded' && cursorFollowsLivePage) {
          nextThreadCursor.value = sub.cursor()
        }
      },
      { immediate: true },
    )
  }

  async function loadMoreThreads(): Promise<void> {
    const cursor = nextThreadCursor.value
    const scope = currentScope()
    if (!cursor || !scope || !threadsSub || isLoadingMoreThreads.value) return

    isLoadingMoreThreads.value = true
    threadsError.value = null
    try {
      const page = await repoReadThreadPage({
        ...scope,
        pageSize: THREAD_PAGE_SIZE,
        cursor,
      })
      cursorFollowsLivePage = false
      olderThreads.value = [...olderThreads.value, ...page.threads]
      nextThreadCursor.value = page.cursor
    } catch (cause) {
      threadsError.value = parseCallableError(cause).message
    } finally {
      isLoadingMoreThreads.value = false
    }
  }

  function openThread(threadId: string): void {
    if (activeThreadId.value === threadId && messagesSub) return

    const scope = currentScope()

    if (pendingUserMessage.value && pendingUserMessage.value.threadId !== threadId) {
      pendingUserMessage.value = null
    }

    stopMessagesSubscription()
    stopPendingActionsSubscription()
    stopPendingQuestionsSubscription()
    stopPendingConnectsSubscription()
    stopActiveThreadSubscription()
    activeThreadId.value = threadId

    if (!scope) {
      subscriptionMessages.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }

    subscriptionMessages.value = loading()
    const sub = repoSubscribeMessages({ ...scope, threadId })
    messagesSub = sub
    stopMessagesWatch = watch(
      sub.state,
      (state) => {
        subscriptionMessages.value =
          state.status === 'loaded' ? loaded(sortByIndex(state.value)) : state
      },
      { immediate: true },
    )

    const threadSub = repoSubscribeThread({ ...scope, threadId })
    activeThreadSub = threadSub
    stopActiveThreadWatch = watch(
      threadSub.state,
      (state) => {
        activeThread.value = state.status === 'loaded' ? state.value : null
      },
      { immediate: true },
    )

    const actionsSub = repoSubscribePendingActions({ ...scope, threadId })
    pendingActionsSub = actionsSub
    stopPendingActionsWatch = watch(
      actionsSub.state,
      (state) => {
        const actions = state.status === 'loaded' ? state.value : []
        actionLinger.track(actions)
        pendingActions.value = actions
      },
      { immediate: true },
    )

    const questionsSub = repoSubscribePendingQuestions({ ...scope, threadId })
    pendingQuestionsSub = questionsSub
    stopPendingQuestionsWatch = watch(
      questionsSub.state,
      (state) => {
        const questions = state.status === 'loaded' ? state.value : []
        questionLinger.track(questions)
        pendingQuestions.value = questions
      },
      { immediate: true },
    )

    const connectsSub = repoSubscribePendingConnects({ ...scope, threadId })
    pendingConnectsSub = connectsSub
    stopPendingConnectsWatch = watch(
      connectsSub.state,
      (state) => {
        const connects = state.status === 'loaded' ? state.value : []
        connectLinger.track(connects)
        pendingConnects.value = connects
      },
      { immediate: true },
    )

    stopReconciliationWatch = watch(subscriptionMessages, (state) => {
      if (state.status !== 'loaded' || !pendingUserMessage.value) return
      const pending = pendingUserMessage.value
      const serverCopy = state.value.find(
        (m) => m.role === 'user' && m.text === pending.text && m.index >= pending.index,
      )
      if (serverCopy) {
        pendingUserMessage.value = null
      }
    })
  }

  function startNewThread(type: ThreadType): void {
    pendingUserMessage.value = null
    stopMessagesSubscription()
    stopPendingActionsSubscription()
    stopPendingQuestionsSubscription()
    stopPendingConnectsSubscription()
    stopActiveThreadSubscription()
    activeThreadId.value = null
    subscriptionMessages.value = loaded([])
    pendingThreadType = type
  }

  function handleStreamChunk(chunk: AgentStreamChunk, stepIndexById: Map<string, number>): void {
    if (sendStatus.value === 'submitting') sendStatus.value = 'streaming'

    if (chunk.type === 'text') {
      stream.push(chunk.text)
      return
    }

    if (chunk.type === 'tool_call') {
      stepIndexById.set(chunk.id, liveSteps.value.length)
      liveSteps.value = [
        ...liveSteps.value,
        {
          id: chunk.id,
          kind: chunk.kind,
          labelKey: chunk.labelKey,
          ...(chunk.labelParams ? { labelParams: chunk.labelParams } : {}),
          status: 'active',
        },
      ]
      return
    }

    if (chunk.type === 'stage') {
      const decision = useStageStore().request(chunk)
      const stepIndex = stepIndexById.get(chunk.id)
      if (!decision.accepted && stepIndex !== undefined) {
        liveSteps.value = liveSteps.value.map((step, i) =>
          i === stepIndex
            ? { ...step, description: decision.detail ?? i18n.global.t('chat.activity.notOpened') }
            : step,
        )
      }
      return
    }

    const index = stepIndexById.get(chunk.id)
    if (index === undefined) return
    liveSteps.value = liveSteps.value.map((step, i) =>
      i === index
        ? {
            ...step,
            status: chunk.ok ? 'complete' : 'failed',
            ...(chunk.sources?.length ? { sources: chunk.sources } : {}),
          }
        : step,
    )
    if (chunk.type === 'tool_result' && chunk.outcome) {
      liveOutcomes.value = [...liveOutcomes.value, chunk.outcome]
    }
  }

  /** The name of the screen the composer's screen chip shows: the staged section or the page the user is on; none when the chat itself is the page. */
  const contextScreen = computed<string | undefined>(() => {
    const staged = useStageStore().context
    if (staged) return staged.label
    const here = useSurfaceStore().current
    return here && here.screen !== 'home' ? here.label : undefined
  })

  /** What the composer chips show right now, in the shape the server stores on the message. */
  function buildMessageContext(): MessageContext | undefined {
    const screen = contextScreen.value
    const item = attachment.value
    if (!screen && !item) return undefined
    const excerpt = item?.chip ? undefined : item?.excerpt?.trim()
    return {
      ...(screen ? { screen: truncate(screen, MESSAGE_CONTEXT_SCREEN_MAX_LENGTH) } : {}),
      ...(item
        ? {
            item: {
              kind: item.kind,
              ref: truncate(item.chip ?? item.ref, VIEW_CONTEXT_REF_MAX_LENGTH),
              ...(excerpt ? { excerpt: truncate(excerpt, VIEW_CONTEXT_EXCERPT_MAX_LENGTH) } : {}),
            },
          }
        : {}),
    }
  }

  /** What the client can see for the turn about to be sent: the ambient screen and, on its own turn only, the attachment; every server cap is enforced again. */
  function buildViewContext(): ViewContext | undefined {
    const surface = useSurfaceStore().current
    if (!surface) return undefined

    const pendingAttachment = attachmentPayloadPending.value ? attachment.value : null

    const state = pendingAttachment?.state
      ?.slice(0, MAX_VIEW_CONTEXT_STATE_ENTRIES)
      .map((fact) => ({
        key: truncate(fact.key, VIEW_CONTEXT_STATE_KEY_MAX_LENGTH),
        value: truncate(fact.value, VIEW_CONTEXT_STATE_VALUE_MAX_LENGTH),
      }))

    return {
      screen: surface.screen,
      ...(surface.item
        ? {
            item: {
              id: truncate(surface.item.id, VIEW_CONTEXT_REF_MAX_LENGTH),
              label: truncate(surface.item.label, VIEW_CONTEXT_REF_MAX_LENGTH),
            },
          }
        : {}),
      ...(state && state.length > 0 ? { state } : {}),
      ...(pendingAttachment ? { attachments: [attachmentToPayload(pendingAttachment)] } : {}),
    }
  }

  interface TurnResolution {
    confirmActionId?: string
    declineActionId?: string
    answerQuestionId?: string
    answerValue?: string
  }

  interface FailedTurn {
    threadId: string
    text: string
    resolution: TurnResolution
    imageAttachments: PendingImageHandoff[]
  }

  async function send(
    text: string,
    resolution: TurnResolution = {},
    imageAttachments: PendingImageHandoff[] = [],
  ): Promise<void> {
    const failure: FailedTurn = {
      threadId: activeThreadId.value ?? '',
      text,
      resolution,
      imageAttachments,
    }
    failedTurn.value = null

    const scope = currentScope()
    if (!scope) {
      sendStatus.value = 'error'
      error.value = i18n.global.t('common.noWorkspaceSelected')
      failedTurn.value = failure
      return
    }

    sendStatus.value = 'submitting'
    lastTurnCompleted.value = false
    error.value = null
    if (attachment.value?.kind === 'design' && attachmentPayloadPending.value) {
      await Promise.all([...designSendGates].map((gate) => gate().catch(() => undefined)))
    }
    useStageStore().beginTurn()

    const viewContext = buildViewContext()
    const isResolution =
      !!resolution.confirmActionId || !!resolution.declineActionId || !!resolution.answerQuestionId
    const messageContext = isResolution ? undefined : buildMessageContext()
    // A document or design chip is spent when its turn is sent; a post chip outlives it.
    if (attachment.value && ONE_SHOT_ATTACHMENT_KINDS.includes(attachment.value.kind)) {
      attachment.value = null
    }
    attachmentPayloadPending.value = false

    clearTurnDisplay()

    const subscriptionState = subscriptionMessages.value
    const highestIndex =
      subscriptionState.status === 'loaded' && subscriptionState.value.length > 0
        ? Math.max(...subscriptionState.value.map((m) => m.index))
        : -1

    revealWatermark.value = highestIndex

    const previewAttachments = imageAttachments.map((image) => ({
      src: image.src,
      alt: image.alt,
    }))

    if (text !== '' || previewAttachments.length > 0) {
      pendingUserMessage.value = {
        id: `${PENDING_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`,
        threadId: activeThreadId.value || '',
        role: 'user',
        text,
        index: highestIndex + 1,
        createdAt: new Date().toISOString(),
        ...(previewAttachments.length > 0 ? { attachments: previewAttachments } : {}),
        ...(messageContext ? { context: messageContext } : {}),
      }
    }

    const controller = new AbortController()
    activeAbortController = controller
    const stepIndexById = new Map<string, number>()

    try {
      const settledAttachments =
        imageAttachments.length > 0
          ? (await Promise.all(imageAttachments.map((image) => image.result))).flatMap((item) =>
              item ? [{ itemId: item.id }] : [],
            )
          : []

      const result = await repoStreamAgentMessage(
        {
          ...(activeThreadId.value ? { threadId: activeThreadId.value } : {}),
          ...(!activeThreadId.value ? { threadType: pendingThreadType } : {}),
          message: text,
          ...(resolution.confirmActionId ? { confirmActionId: resolution.confirmActionId } : {}),
          ...(resolution.declineActionId ? { declineActionId: resolution.declineActionId } : {}),
          ...(resolution.answerQuestionId
            ? { answerQuestionId: resolution.answerQuestionId, answerValue: resolution.answerValue }
            : {}),
          ...(viewContext ? { viewContext } : {}),
          ...(messageContext ? { messageContext } : {}),
          ...(settledAttachments.length > 0 ? { attachments: settledAttachments } : {}),
        },
        {
          signal: controller.signal,
          onChunk: (chunk) => handleStreamChunk(chunk, stepIndexById),
        },
      )

      if (!activeThreadId.value) {
        if (pendingUserMessage.value) {
          pendingUserMessage.value = {
            ...pendingUserMessage.value,
            threadId: result.threadId,
          }
        }
        openThread(result.threadId)
      }

      await stream.drain()
      clearTurnDisplay()
      sendStatus.value = 'ready'
      lastTurnCompleted.value = true
    } catch (cause) {
      clearTurnDisplay()
      if (isChatStreamAbortedError(cause)) {
        sendStatus.value = 'ready'
      } else {
        const parsed = parseCallableError(cause)
        sendStatus.value = 'error'
        error.value = isChatRateLimitedReason(parsed.reason)
          ? i18n.global.t('chat.errors.rateLimited')
          : parsed.message
        pendingUserMessage.value = null
        failedTurn.value = failure
      }
    } finally {
      activeAbortController = null
      if (sendStatus.value === 'submitting') sendStatus.value = 'ready'
    }
  }

  /** Answers an action Raldo is waiting on; the turn carries no message. */
  async function confirmAction(actionId: string): Promise<void> {
    await send('', { confirmActionId: actionId })
  }

  async function declineAction(actionId: string): Promise<void> {
    await send('', { declineActionId: actionId })
  }

  /** Answers a question Raldo asked; the answer travels as the turn's message with the question ID alongside. */
  async function answerQuestion(questionId: string, value: string): Promise<void> {
    await send(value, { answerQuestionId: questionId, answerValue: value })
  }

  /** Hides a connect card for the rest of the session; a local read of `visibleConnect`, never a write. */
  function dismissConnect(connectId: string): void {
    dismissedConnectIds.value = new Set(dismissedConnectIds.value).add(connectId)
  }

  function stop(): void {
    activeAbortController?.abort()
    clearTurnDisplay()
  }

  async function retry(): Promise<void> {
    const turn = failedTurn.value
    if (!canRetry.value || turn === null) return
    await send(turn.text, turn.resolution, turn.imageAttachments)
  }

  /** Brings a passage of a brand document into the chat as a chip plus a `selection` attachment; `state` is read verbatim from the caller. */
  function askAboutDocumentPassage(input: {
    title: string
    quote: string
    state?: ViewContextStateFact[]
  }): void {
    attachment.value = {
      kind: 'document',
      ref: input.title,
      ...(input.quote.trim() ? { excerpt: input.quote } : {}),
      ...(input.state ? { state: input.state } : {}),
    }
    attachmentPayloadPending.value = true
  }

  /** Sends a message about a brand document: a `document` attachment plus the user's own message. */
  function askAboutDocument(input: {
    title: string
    message: string
    state?: ViewContextStateFact[]
  }): void {
    attachment.value = {
      kind: 'document',
      ref: input.title,
      ...(input.state ? { state: input.state } : {}),
    }
    attachmentPayloadPending.value = true
    void send(input.message)
  }

  /** Sends a message about a post: an `action` attachment plus the user's own message. */
  function askAboutPost(input: {
    title: string
    message: string
    state?: ViewContextStateFact[]
  }): void {
    attachment.value = {
      kind: 'post',
      ref: input.title,
      ...(input.state ? { state: input.state } : {}),
    }
    attachmentPayloadPending.value = true
    void send(input.message)
  }

  /** Brings a passage of the post being written into the chat as a chip plus a `selection` attachment. */
  function askAboutPostPassage(input: {
    title: string
    quote: string
    state?: ViewContextStateFact[]
  }): void {
    attachment.value = {
      kind: 'post',
      ref: input.title,
      ...(input.quote.trim() ? { excerpt: input.quote } : {}),
      ...(input.state ? { state: input.state } : {}),
    }
    attachmentPayloadPending.value = true
  }

  /** Sends a question about an Insights panel: an `insight` attachment naming it, and the seeded question as the user's message. */
  function askAboutInsights(input: {
    ref: string
    message: string
    state?: ViewContextStateFact[]
  }): void {
    attachment.value = {
      kind: 'insight',
      ref: input.ref,
      ...(input.state ? { state: input.state } : {}),
    }
    attachmentPayloadPending.value = true
    void send(input.message)
  }

  /** Points the next message at a page, or an element of it, in the design editor: a chip plus a `design` attachment carrying the element's HTML. */
  function askAboutDesign(input: DesignSelectionInput): void {
    const { chip, excerpt } = designSelectionText(input)
    attachment.value = {
      kind: 'design',
      ref: input.name,
      excerpt,
      chip,
      state: [
        { key: 'designId', value: input.designId },
        { key: 'page', value: String(input.page) },
        ...(input.eid ? [{ key: 'eid', value: input.eid }] : []),
      ],
    }
    attachmentPayloadPending.value = true
  }

  /** Registers work that must finish before a message about a design is sent, such as saving the editor's pending edits; returns the way to unregister it. */
  function beforeDesignSend(gate: () => Promise<void>): () => void {
    designSendGates.add(gate)
    return () => designSendGates.delete(gate)
  }

  /** Puts words in the composer for the user to send or change. */
  function setDraft(text: string): void {
    draft.value = text
  }

  /** The words waiting for the composer, taken once. */
  function takeDraft(): string | null {
    const text = draft.value
    draft.value = null
    return text
  }

  function clearAttachment(): void {
    attachment.value = null
    attachmentPayloadPending.value = false
  }

  function titleFor(threadId: string, known?: string): string {
    if (known) return known
    const state = threads.value
    const listed =
      state.status === 'loaded' ? state.value.find((t) => t.id === threadId) : undefined
    return listed?.title || activeThread.value?.title || i18n.global.t('chat.untitledChat')
  }

  function isAddressedByRoute(threadId: string): boolean {
    const current = router.currentRoute.value
    return current.name === 'chat' && current.params.id === threadId
  }

  /** Confirms, then deletes a conversation; resolves `true` once the server has taken it. */
  async function deleteThread(threadId: string, title?: string): Promise<boolean> {
    const name = titleFor(threadId, title)
    const confirmed = await useDialogStore().confirm({
      title: i18n.global.t('chat.deleteDialog.title', { name }),
      description: i18n.global.t('chat.deleteDialog.description'),
      confirmLabel: i18n.global.t('common.delete'),
      tone: 'destructive',
    })
    if (!confirmed) return false

    try {
      await repoDeleteAgentThread({ threadId })
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(
        i18n.global.t('chat.errors.deleteFailed'),
        isThreadOwnerMismatchReason(parsed.reason)
          ? i18n.global.t('chat.errors.notYours')
          : parsed.message,
      )
      return false
    }

    if (activeThreadId.value === threadId) {
      stop()
      startNewThread('chat')
      if (isAddressedByRoute(threadId)) await router.push('/')
    }

    return true
  }

  function closeAll(): void {
    stop()
    stopThreadsSubscription()
    pendingUserMessage.value = null
    stopMessagesSubscription()
    stopPendingActionsSubscription()
    stopPendingQuestionsSubscription()
    stopPendingConnectsSubscription()
    stopActiveThreadSubscription()
    activeThreadId.value = null
    livePage.value = loading()
    subscriptionMessages.value = loaded([])
    sendStatus.value = 'ready'
    lastTurnCompleted.value = false
    error.value = null
    failedTurn.value = null
    attachment.value = null
    attachmentPayloadPending.value = false
    clearTurnDisplay()
  }

  onScopeDispose(onRetainedTeardown(closeAll))

  return {
    threads,
    hasMoreThreads,
    isLoadingMoreThreads,
    threadsError,
    activeThread,
    isActiveThreadClosed,
    activeThreadId,
    messages,
    sendStatus,
    activity,
    error,
    canRetry,
    attachment,
    contextScreen,
    streamingText,
    liveSteps,
    liveOutcomes,
    openThreads,
    loadMoreThreads,
    openThread,
    startNewThread,
    deleteThread,
    send,
    confirmAction,
    declineAction,
    answerQuestion,
    askAboutDesign,
    beforeDesignSend,
    draft,
    setDraft,
    takeDraft,
    askAboutDocument,
    askAboutDocumentPassage,
    askAboutInsights,
    askAboutPost,
    askAboutPostPassage,
    clearAttachment,
    pendingActions,
    visibleAction,
    pendingQuestions,
    visibleQuestion,
    pendingConnects,
    visibleConnect,
    dismissConnect,
    stop,
    retry,
    closeAll,
  }
})
